// hooks/useShootingLogic.ts
import useConditionalTimeout from 'beautiful-react-hooks/useConditionalTimeout';
import {
  SECOND_PER_COUNTDOWN,
  SHOOTING_TYPES,
  TIME_LIMIT_DEFAULT_COUNTDOWN,
  TIME_TO_SHOW_PHOTO,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { dv } from 'helpers/common.helper';
import { isHigher } from 'helpers/math.helper';
import { isEqualVal } from 'helpers/string.helper';
import { size } from 'lodash';
import { CameraModel } from 'models/photo/camera.model';
import React, { useContext, useMemo, useState } from 'react';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

export const Context = React.createContext<ShootingContextReturnTypes>({
  currentCameraId: 0,
  availableListCameras: [],
  isCameraSessionOpen: false,
  isCountDownMode: false,
  isFinishTakePhoto: false,
  isInDuringTakePhoto: false,
  isVerticalFrame: false,
  isDisplayOnLiveview: false,
  numberOfTakePicture: 0,
  sizeOfNeedToTakePhoto: 0,
  takingPhotoIndex: 0,
  countDownTakePictureSecond: 0,
  delayCountDownTakePictureSecond: 0,
  timeLimitTakePictureSecond: 0,

  countDownSec: 0,
  isStartCountDownShooting: false,
  isWaitingApiShooting: false,
  isFlashCamera: false,
  isDelayShooting: false,
  selectImageIndex: 0,
  listIndexOfRetake: [],
  imageHash: 0,

  setCountDownSec(value: React.SetStateAction<number>): void {
    throw new Error('Function not implemented.');
  },
  setIsStartCountDownShooting(value: React.SetStateAction<boolean>): void {
    throw new Error('Function not implemented.');
  },
  setIsWaitingApiShooting(value: React.SetStateAction<boolean>): void {
    throw new Error('Function not implemented.');
  },
  setIsFlashCamera(value: React.SetStateAction<boolean>): void {
    throw new Error('Function not implemented.');
  },
  setIsDelayShooting(value: React.SetStateAction<boolean>): void {
    throw new Error('Function not implemented.');
  },
  setSelectImageIndex(value: React.SetStateAction<number>): void {
    throw new Error('Function not implemented.');
  },
  setListIndexOfRetake(value: React.SetStateAction<number[]>): void {
    throw new Error('Function not implemented.');
  },
  setImageHash(value: React.SetStateAction<number>): void {
    throw new Error('Function not implemented.');
  },
});

interface ShootingContextProps {}
type ShootingContextReturnTypes = {
  currentCameraId: CameraModel['ID'];
  availableListCameras: Array<CameraModel>;
  isCameraSessionOpen: boolean;
  isCountDownMode: boolean;
  isFinishTakePhoto: boolean;
  isInDuringTakePhoto: boolean;
  isVerticalFrame: boolean;
  isDisplayOnLiveview: boolean;
  numberOfTakePicture: number;
  sizeOfNeedToTakePhoto: number;
  takingPhotoIndex: number;
  countDownTakePictureSecond: number;
  delayCountDownTakePictureSecond: number;
  timeLimitTakePictureSecond: number;

  countDownSec: number;
  isStartCountDownShooting: boolean;
  isWaitingApiShooting: boolean;
  isFlashCamera: boolean;
  isDelayShooting: boolean;
  selectImageIndex: number;
  listIndexOfRetake: Array<number>;
  imageHash: number;

  setCountDownSec: React.Dispatch<React.SetStateAction<number>>;
  setIsStartCountDownShooting: React.Dispatch<React.SetStateAction<boolean>>;
  setIsWaitingApiShooting: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFlashCamera: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDelayShooting: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectImageIndex: React.Dispatch<React.SetStateAction<number>>;
  setListIndexOfRetake: React.Dispatch<React.SetStateAction<Array<number>>>;
  setImageHash: React.Dispatch<React.SetStateAction<number>>;
};

export const ShootingContext: React.FC<
  React.PropsWithChildren<ShootingContextProps>
> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { listCameras, listPhotoTakens, currentLayout, themeDetail } =
    useBoothAppContext();
  const {
    selectedCameraId,
    shootingModeId,
    isRetakePhoto,
    isShootingOfRetakingPhoto,
    wasRetakePhoto,
  } = useAppSelector((state) => state.selfeApp);
  const { boothInfo, isThemeBeforePayment } = useAppSelector((s) => s.booth);

  // countdown config
  const countDownTakePictureSecond = useMemo(
    () => boothInfo?.TimeCountDown || SECOND_PER_COUNTDOWN,
    [boothInfo?.TimeCountDown],
  );
  const delayCountDownTakePictureSecond = useMemo(
    () => boothInfo?.DelayTimeCountDown || 0,
    [boothInfo?.DelayTimeCountDown],
  );
  const timeLimitTakePictureSecond = useMemo(
    () => boothInfo?.TimeLimit || TIME_LIMIT_DEFAULT_COUNTDOWN,
    [boothInfo?.TimeLimit],
  );

  // local states
  const [countDownSec, setCountDownSec] = useState(countDownTakePictureSecond);
  const [isStartCountDownShooting, setIsStartCountDownShooting] =
    useState(false);
  const [isWaitingApiShooting, setIsWaitingApiShooting] = useState(false);
  const [isFlashCamera, setIsFlashCamera] = useState(false);
  const [isDelayShooting, setIsDelayShooting] = useState(false);
  const [selectImageIndex, setSelectImageIndex] = useState(0);
  const [listIndexOfRetake, setListIndexOfRetake] = useState<number[]>([]);
  // image
  const [imageHash, setImageHash] = useState(Date.now());

  // Thêm state để lưu mapping giữa ảnh và theme
  // const [photoThemes, setPhotoThemes] = useState<Record<number, number>>({});
  // const [themeOffset, setThemeOffset] = useState<number>(0);
  // const [isViewingResultPhoto, setIsViewingResultPhoto] = useState(false);

  // Các camera avaiable
  const availableListCameras = useMemo(
    () => listCameras?.data || [],
    [listCameras],
  );

  // Tổng số lượng ảnh chụp
  const numberOfTakePicture = useMemo(
    () => dv(currentLayout?.numberOfTakePicture, 0),
    [currentLayout?.numberOfTakePicture],
  );

  // Số lượng ảnh đã chụp
  const sizeOfNeedToTakePhoto = useMemo(
    () =>
      isRetakePhoto
        ? size(listPhotoTakens?.data) - size(listIndexOfRetake)
        : size(listPhotoTakens?.data),
    [isRetakePhoto, listPhotoTakens?.data, listIndexOfRetake],
  );

  // index của ảnh đang chụp
  const takingPhotoIndex = useMemo(() => {
    if (!isShootingOfRetakingPhoto && isRetakePhoto) return selectImageIndex;

    return isRetakePhoto ? listIndexOfRetake?.[0] : size(listPhotoTakens?.data);
  }, [
    isShootingOfRetakingPhoto,
    isRetakePhoto,
    selectImageIndex,
    listIndexOfRetake,
    listPhotoTakens?.data,
  ]);

  const isCameraSessionOpen = useMemo(
    () => !!availableListCameras?.[0]?.SessionOpen,
    [availableListCameras],
  );

  const currentCameraId = useMemo(
    () =>
      selectedCameraId || (availableListCameras?.[0]?.ID as CameraModel['ID']),
    [availableListCameras, selectedCameraId],
  );

  // chế độ đếm ngược
  const isCountDownMode = useMemo(
    () =>
      !shootingModeId || isEqualVal(shootingModeId, SHOOTING_TYPES.COUNTDOWN),
    [shootingModeId],
  );

  // chế độ đếm ngược
  const isDisplayOnLiveview = useMemo(
    () => !!(isThemeBeforePayment && themeDetail?.isDisplayOnLiveview),
    [isThemeBeforePayment, themeDetail?.isDisplayOnLiveview],
  );

  // Khi đang trong giai đoạn chụp ảnh, hiện liveview
  const isInDuringTakePhoto = useMemo(
    () =>
      !isDelayShooting &&
      !isFlashCamera &&
      !isWaitingApiShooting &&
      countDownSec > 0 &&
      ((isCountDownMode && isStartCountDownShooting && !isRetakePhoto) ||
        (isCountDownMode &&
          isRetakePhoto &&
          isShootingOfRetakingPhoto &&
          !wasRetakePhoto)),
    [
      countDownSec,
      isCountDownMode,
      isDelayShooting,
      isFlashCamera,
      isRetakePhoto,
      isShootingOfRetakingPhoto,
      isStartCountDownShooting,
      isWaitingApiShooting,
      wasRetakePhoto,
    ],
  );

  // Khi trong giai đoạn kết thúc chụp ảnh, hiện result ảnh
  const isFinishTakePhoto = useMemo(
    () =>
      (!isRetakePhoto && sizeOfNeedToTakePhoto >= numberOfTakePicture) ||
      (isRetakePhoto &&
        isShootingOfRetakingPhoto &&
        sizeOfNeedToTakePhoto >= numberOfTakePicture) ||
      (isRetakePhoto && wasRetakePhoto),
    [
      isRetakePhoto,
      isShootingOfRetakingPhoto,
      numberOfTakePicture,
      sizeOfNeedToTakePhoto,
      wasRetakePhoto,
    ],
  );

  // Màn hình dọc
  const isVerticalFrame = useMemo(
    () =>
      isHigher(currentLayout?.ratioY, currentLayout?.ratioX) ||
      isEqualVal(currentLayout?.ratioY, currentLayout?.ratioX),
    [currentLayout],
  );

  // Show kết quả chụp ảnh
  useConditionalTimeout(
    () => dispatch(setSelfeAppStateAction({ isRetakePhoto: true })),
    TIME_TO_SHOW_PHOTO,
    !isRetakePhoto && sizeOfNeedToTakePhoto >= numberOfTakePicture,
  );

  // Show kết quả chụp ảnh lần 2 (show ảnh chụp lại)
  useConditionalTimeout(
    () =>
      dispatch(
        setSelfeAppStateAction({
          wasRetakePhoto: true,
          isShootingOfRetakingPhoto: false,
        }),
      ),
    TIME_TO_SHOW_PHOTO,
    isRetakePhoto &&
      isShootingOfRetakingPhoto &&
      sizeOfNeedToTakePhoto >= numberOfTakePicture,
  );

  return (
    <Context.Provider
      value={useMemo(
        () => ({
          currentCameraId,
          availableListCameras,
          isCameraSessionOpen,
          isCountDownMode,
          isFinishTakePhoto,
          isInDuringTakePhoto,
          isVerticalFrame,
          isDisplayOnLiveview,
          numberOfTakePicture,
          sizeOfNeedToTakePhoto,
          takingPhotoIndex,
          countDownTakePictureSecond,
          delayCountDownTakePictureSecond,
          timeLimitTakePictureSecond,

          countDownSec,
          isStartCountDownShooting,
          isWaitingApiShooting,
          isFlashCamera,
          isDelayShooting,
          selectImageIndex,
          listIndexOfRetake,
          imageHash,

          setCountDownSec,
          setIsStartCountDownShooting,
          setIsWaitingApiShooting,
          setIsFlashCamera,
          setIsDelayShooting,
          setSelectImageIndex,
          setListIndexOfRetake,
          setImageHash,
        }),
        [
          currentCameraId,
          availableListCameras,
          isCameraSessionOpen,
          isCountDownMode,
          isFinishTakePhoto,
          isInDuringTakePhoto,
          isVerticalFrame,
          isDisplayOnLiveview,
          numberOfTakePicture,
          sizeOfNeedToTakePhoto,
          takingPhotoIndex,
          countDownTakePictureSecond,
          delayCountDownTakePictureSecond,
          timeLimitTakePictureSecond,

          countDownSec,
          isStartCountDownShooting,
          isWaitingApiShooting,
          isFlashCamera,
          isDelayShooting,
          selectImageIndex,
          listIndexOfRetake,
          imageHash,

          setCountDownSec,
          setIsStartCountDownShooting,
          setIsWaitingApiShooting,
          setIsFlashCamera,
          setIsDelayShooting,
          setSelectImageIndex,
          setListIndexOfRetake,
          setImageHash,
        ],
      )}
    >
      {children}
    </Context.Provider>
  );
};

export const useShootingContext = () => useContext(Context);
